import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputComponent } from '../../../components/InputComponent';
import * as generic from '../../../components/Generico';
import { EmpleadoEconomica as EmpleadoEconomicaModel } from '../../../models/ficha/EmpleadoEconomica';
import { EmpleadoService } from '../../../service/EmpleadoService';
import { BottomActionBar } from '../../../components/ui/BottomActionBar';
import { InputActividadEconomica } from '../../../components/ui/Formularios/InputActividadEconomica';

export const EmpleadoEconomica = ({ toast, id, permss, catalogos, tieneConyuge, handlePrev, next, soloLectura }) => {

    const omodel = new EmpleadoEconomicaModel({ tipoFrmEmpresa: catalogos?.tipoFrmEmpresa });
    const defaultValues = generic.funDefault(omodel.model);
    const { control, formState: { errors }, handleSubmit, reset, setError, watch, setValue } = useForm({ defaultValues, shouldTrim: true });
    const empleadoService = new EmpleadoService();
    const { inNumber, inText } = InputComponent({ omodel, errors, control, permss, id, soloLectura });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [siFuente, setSiFuente] = useState(false);
    const [siFuenteCy, setSiFuenteCy] = useState(false);

    const actvInmuebles = Number(watch('ACTV_INMUEBLES')) || 0;
    const actvMuebles = Number(watch('ACTV_MUEBLES')) || 0;
    const actvEfectivo = Number(watch('ACTV_EFECTIVO')) || 0;
    const actvInversiones = Number(watch('ACTV_INVERSIONES')) || 0;
    const actvCreditos = Number(watch('ACTV_CREDITOS')) || 0;
    const actvOtros = Number(watch('ACTV_OTROS')) || 0;
    const pasvBancarios = Number(watch('PASV_BANCARIOS')) || 0;
    const pasvHipotecarios = Number(watch('PASV_HIPOTECARIOS')) || 0;
    const pasvQuirografario = Number(watch('PASV_QUIROGRAFARIO')) || 0;
    const pasvCredito = Number(watch('PASV_CREDITO')) || 0;
    const pasvOtrasCtas = Number(watch('PASV_OTRAS_CTAS')) || 0;
    const pasvOtrosPasv = Number(watch('PASV_OTROS_PASV')) || 0;

    useEffect(() => {
        const totalActivos = actvInmuebles + actvMuebles + actvEfectivo + actvInversiones + actvCreditos + actvOtros;
        const totalPasivos = pasvBancarios + pasvHipotecarios + pasvQuirografario + pasvCredito + pasvOtrasCtas + pasvOtrosPasv;
        setValue('activos', totalActivos);
        setValue('pasivos', totalPasivos);
        setValue('patrimonio', totalActivos - totalPasivos);
    }, [actvInmuebles, actvMuebles, actvEfectivo, actvInversiones, actvCreditos, actvOtros,
        pasvBancarios, pasvHipotecarios, pasvQuirografario, pasvCredito, pasvOtrasCtas, pasvOtrosPasv, setValue]);

    useEffect(() => {
        editFicha();
        // eslint-disable-next-line
    }, [id]);

    const editFicha = () => {
        setIsLoading(true);
        empleadoService.consultarEmpleado(id, 'seccion3').then(res => {
            const _emp = res.data.empleado;
            if (!_emp) {
                reset(defaultValues);
                setIsLoading(false);
                return;
            }
            setSiFuente(!!_emp.fuentes_ingresos_empleado);
            setSiFuenteCy(!!_emp.fuentes_ingresos_conyugue);
            reset(generic.renewValues(_emp, omodel.model));
        }).finally(() => setIsLoading(false));
    }

    const onSubmit = (data) => {
        setIsSaving(true);
        data.id = id;
        data.indice = 2;
        data.casado = catalogos?.casado;
        data.union = catalogos?.union;
        data.tipoFrmEmpresa = catalogos?.tipoFrmEmpresa;
        data.seccion = 'seccion3';

        empleadoService.editarEmpleado(data).then(res => {
            generic.funcSuccess(res, toast);
            next();
        }).catch(error => {
            generic.funcError(error, toast, setError);
        }).finally(() => setIsSaving(false));
    }

    const onError = () => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Se detectaron errores, por favor revise el formulario para corregir.', life: 3000 });
    }

    if (isLoading) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-5">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
                <span className="mt-3 text-color-secondary font-medium text-lg">Consultando información...</span>
            </div>
        );
    }

    const onChangeOtroIngreso = (e, esCy) => {
        const value = e.value;
        if (esCy) {
            setSiFuenteCy(!!value);
        } else {
            setSiFuente(!!value);
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit, onError)} className="p-fluid">

            <Panel header={generic.renderPanelHeader('D', 'INFORMACIÓN ECONÓMICA', 'pi pi-wallet')} className="panel-ficha shadow-1 mb-4">
                <div className="col-12 mb-3">
                    <div className="flex align-items-center">
                        <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                            <i className="pi pi-user text-primary"></i>
                            INGRESOS Y EGRESOS DEL EMPLEADO
                        </span>
                        <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                    </div>
                </div>

                <div className="formgrid grid">
                    {inNumber('ingreso_mensual_dependencia_empleado', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left' }, siFuente ? 4 : 6)}
                    {inNumber('otros_ingreso_empleado', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left', func: onChangeOtroIngreso, vars: false }, siFuente ? 4 : 6)}
                    {siFuente && inText('fuentes_ingresos_empleado', { icon: 'pi pi-pencil' }, 4)}
                    {siFuente && (
                        <InputActividadEconomica name="otra_actividad_economica" control={control} modelDef={omodel.model['otra_actividad_economica']} disabled={soloLectura} col={12} />
                    )}
                    {inNumber('gasto_individual', 2, true, { icon: 'pi pi-shopping-cart', iconPosition: 'left' }, 12)}
                </div>

                {tieneConyuge && (
                    <>
                        <div className="col-12 mb-3 mt-4">
                            <div className="flex align-items-center">
                                <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                                    <i className="pi pi-heart text-primary"></i>
                                    INGRESOS DEL CÓNYUGE / CONVIVIENTE
                                </span>
                                <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                            </div>
                        </div>

                        <div className="formgrid grid">
                            {inNumber('ingreso_mensual_conyugue', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left' }, siFuenteCy ? 4 : 6)}
                            {inNumber('otros_ingreso_conyugue', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left', func: onChangeOtroIngreso, vars: true }, siFuenteCy ? 4 : 6)}
                            {siFuenteCy && inText('fuentes_ingresos_conyugue', { icon: 'pi pi-pencil' }, 4)}
                            {siFuenteCy && (
                                <InputActividadEconomica name="otra_actividad_economica_conyugue" control={control} modelDef={omodel.model['otra_actividad_economica_conyugue']} disabled={soloLectura} col={12} />
                            )}
                        </div>
                    </>
                )}
            </Panel>

            <Panel header={generic.renderPanelHeader('E', 'DECLARACIÓN PATRIMONIAL', 'pi pi-chart-line')} className="panel-ficha shadow-1 mb-4">
                <div className="grid formgrid mt-2">
                    <div className="col-12 md:col-6 mb-3 md:mb-0">
                        <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1 border-1 border-300">
                            <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">ACTIVOS</div>
                            <div className="formgrid grid flex-grow-1 align-content-start">
                                {inNumber('ACTV_INMUEBLES', 2, true, { icon: 'pi pi-home', iconPosition: 'left' }, 12)}
                                {inNumber('ACTV_MUEBLES', 2, true, { icon: 'pi pi-car', iconPosition: 'left' }, 12)}
                                {inNumber('ACTV_EFECTIVO', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left' }, 12)}
                                {inNumber('ACTV_INVERSIONES', 2, true, { icon: 'pi pi-chart-line', iconPosition: 'left' }, 12)}
                                {inNumber('ACTV_CREDITOS', 2, true, { icon: 'pi pi-file', iconPosition: 'left' }, 12)}
                                {inNumber('ACTV_OTROS', 2, true, { icon: 'pi pi-box', iconPosition: 'left' }, 12)}
                            </div>

                            <div className="mt-3">
                                <Divider type="dashed" className="my-2" />
                                <div className="bg-blue-50 p-2 border-round border-1 border-blue-200 formgrid grid mx-0">
                                    {inNumber('activos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true, obl: false }, 12)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1 border-1 border-300">
                            <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">PASIVOS</div>
                            <div className="formgrid grid flex-grow-1 align-content-start">
                                {inNumber('PASV_BANCARIOS', 2, true, { icon: 'pi pi-calendar-minus', iconPosition: 'left' }, 12)}
                                {inNumber('PASV_HIPOTECARIOS', 2, true, { icon: 'pi pi-home', iconPosition: 'left' }, 12)}
                                {inNumber('PASV_QUIROGRAFARIO', 2, true, { icon: 'pi pi-file', iconPosition: 'left' }, 12)}
                                {inNumber('PASV_CREDITO', 2, true, { icon: 'pi pi-credit-card', iconPosition: 'left' }, 12)}
                                {inNumber('PASV_OTRAS_CTAS', 2, true, { icon: 'pi pi-file', iconPosition: 'left' }, 12)}
                                {inNumber('PASV_OTROS_PASV', 2, true, { icon: 'pi pi-box', iconPosition: 'left' }, 12)}
                            </div>

                            <div className="mt-3">
                                <Divider type="dashed" className="my-2" />
                                <div className="bg-blue-50 p-2 border-round border-1 border-blue-200 formgrid grid mx-0">
                                    {inNumber('pasivos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true, obl: false }, 12)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid mt-4">
                    <div className="col-12 md:col-6 md:col-offset-3">
                        <div className="bg-blue-100 p-3 border-round border-1 border-blue-300 shadow-2 formgrid grid mx-0">
                            {inNumber('patrimonio', 2, true, { icon: 'pi pi-wallet', iconPosition: 'left', disabled: true, obl: false, min: 'nulo' }, 12)}
                        </div>
                    </div>
                </div>
            </Panel>

            <BottomActionBar onPrev={handlePrev} onNext={next} loading={isSaving} isReadOnly={soloLectura} />
        </form>
    );
}
